/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import 'moment-timezone';
import HeatMap from './Heatmap';
import Trends from './Trends';
import Comparison from './Comparison';
import ZoomedHeatMap from './ZoomedHeatmap';
import { getDevicesSummaryOverTime } from '../../services/dashboard-service';
import { transformDataForPlotByDevice } from '../../_helpers/graph';
import {
  GraphLegendGroups,
  ParametersType,
  PlotTransformedDataType,
  TimeSpan,
  DeviceType
} from '../../constants/telosair-interfaces';
import { useData } from '../../context/data.context';
import { changeZeroValuesToNull, getParameterByName } from '../../_helpers/globalHelper';
import { COLORS, POINT_STYLES } from '../../constants/global-constants';
import { Filters, getAppliedFilter, setAppliedFilter } from '../../services/localstorage.service';
import ScatterPlot from './scatterPlot';
import Deque from '../../classes/deque';

type zoomedChartTypes = 'heatmap' | 'trend' | 'comparison' | 'scatter';

type RawDataProps = {
  selectedDevices: { label: string; value: number }[];
  selectedParameters: ParametersType[];
  refreshing: boolean;
  timeSpan: TimeSpan;
  timeSpanDate: {
    startDate: number;
    endDate: number;
  };
};

/**
 * RawData renders the Data in Raw Form.
 * @param props - Properties of Rawdata.
 * @returns - RawData Page.
 */
export default function RawData(props: RawDataProps): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pointUsedRef = useRef(0);
  const { selectedDevices, selectedParameters, timeSpanDate, refreshing, timeSpan } = props;
  const { userConfigData, parameters, devices, loading } = useData();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [zoomedChartType, setZoomedChartType] = useState<zoomedChartTypes | null>(null);
  const [trendsLabel, setTrendsLabels] = useState<Record<string, string>>({});
  const [scatterPlotData, setScatterPlotData] = useState<PlotTransformedDataType>({});
  const [chartGraphData, setChartGraphData] = useState<Record<string, Deque>>({});
  const [selectedGroupLegends, setSelectedGroupLegends] = useState<Record<string, string[]>>(
    {} as any
  );
  const [groupElements, setGroupElements] = useState<GraphLegendGroups>({} as any);
  const [deviceType, setDeviceType] = useState<DeviceType>(DeviceType.MIXED);

  useEffect(() => {
    const selectedDeviceTypes = selectedDevices
      .map((deviceq) => devices.find((device) => device.serialNumber === deviceq.value)?.deviceType)
      .filter(Boolean);

    if (selectedDeviceTypes.length > 0) {
      const uniqueTypes = Array.from(new Set(selectedDeviceTypes));
      setDeviceType(uniqueTypes.length === 1 ? (uniqueTypes[0] as DeviceType) : DeviceType.MIXED);
    }
  }, [selectedDevices]);
  /**
   * Fetches getChartData api.
   */
  const fetchData = async (): Promise<void> => {
    try {
      const { data } = await getDevicesSummaryOverTime({
        startDate: timeSpanDate.startDate,
        endDate: timeSpanDate.endDate,
        devices: selectedDevices.map((device) => device.value)
      });
      const newLineChartGraphData: Record<string, Deque> = {};
      const tempTrendsLabel: Record<string, string> = {};
      Object.entries(data.data).forEach(([key, deviceSummary]: any, index) => {
        deviceSummary.forEach((item: { data: number[][]; name: ParametersType }) => {
          const dequeData = new Deque();
          const value = changeZeroValuesToNull(item.data);
          // console.log({ value, name: item.name, key, data });
          dequeData.push(...value);
          const dequeKey = `${key}:${item.name}`;
          newLineChartGraphData[`${key}:${item.name}`] = dequeData;
          tempTrendsLabel[dequeKey] = getParameterByName(item.name, parameters)?.shortName || '-';
        });
      });
      setScatterPlotData(transformDataForPlotByDevice(data.data));
      setChartGraphData(newLineChartGraphData);
      setTrendsLabels(tempTrendsLabel);
      setIsDataLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      setIsDataLoading(false);
    }
  };
  useEffect(() => {
    if (selectedDevices.length > 0) {
      setIsDataLoading(true);
      fetchData();
    } else {
      setChartGraphData({});
      setIsDataLoading(false);
    }
  }, [selectedDevices, timeSpanDate]);

  useEffect(() => {
    pointUsedRef.current = 0;
    setGroupElements({
      parameter: parameters.map((parameter, index) => ({
        label: parameter?.shortName || '',
        value: parameter?.parameterName || '',
        color: COLORS[index],
        active: selectedParameters.includes(parameter.parameterName)
      })),
      device: devices.map((device) => {
        const exists = selectedDevices.some(
          (selectedDevice) => selectedDevice.value === device.serialNumber
        );
        let pointStyle = POINT_STYLES[0];
        if (exists) {
          pointStyle = POINT_STYLES[pointUsedRef.current % 8];
          pointUsedRef.current += 1;
        }

        return {
          label: device.deviceName,
          value: String(device.serialNumber),
          active: exists,
          color: '#D0D5DD',
          pointStyle,
          pointRadius: 5
        };
      })
    });
    // Restore the Trends legend selection from the last use, keeping only
    // entries that are still part of the current parameter/device selection.
    const validParameterValues = selectedParameters.map(String);
    const validDeviceValues = selectedDevices.map((device) => String(device.value));
    const storedLegends = getAppliedFilter(Filters.dataTrendsLegends)?.data as
      | { parameter?: string[]; device?: string[] }
      | undefined;
    const restoredParameters = (storedLegends?.parameter || []).filter((value) =>
      validParameterValues.includes(value)
    );
    const restoredDevices = (storedLegends?.device || []).filter((value) =>
      validDeviceValues.includes(value)
    );
    let parameterLegend: string[] = restoredParameters;
    if (parameterLegend.length === 0) {
      parameterLegend = selectedParameters.length > 0 ? [selectedParameters[0]] : [];
    }
    setSelectedGroupLegends({
      parameter: parameterLegend,
      device: restoredDevices.length > 0 ? restoredDevices : validDeviceValues
    });
  }, [parameters, devices, selectedDevices, selectedParameters]);

  // Persist the Trends legend selection so it is retained across visits/refreshes.
  useEffect(() => {
    if (
      selectedGroupLegends &&
      (selectedGroupLegends.parameter !== undefined || selectedGroupLegends.device !== undefined)
    ) {
      setAppliedFilter({ key: Filters.dataTrendsLegends, data: selectedGroupLegends });
    }
  }, [selectedGroupLegends]);

  return (
    <>
      {!zoomedChartType && (
        <div className="flex flex-wrap justify-between transition-all mb-4 mt-2">
          <HeatMap
            selectedParameters={selectedParameters}
            onZoom={() => setZoomedChartType('heatmap')}
          />
          <Trends
            selectedParameters={selectedParameters}
            onZoom={() => setZoomedChartType('trend')}
            isDataLoading={isDataLoading || loading}
            selectedGroupLegends={selectedGroupLegends}
            setSelectedGroupLegends={setSelectedGroupLegends}
            groupElements={groupElements}
            groupStructure="device:parameter"
            labels={trendsLabel}
            timeSpan={timeSpan}
            lineChartGraphData={chartGraphData}
            type={deviceType}
          />
          <Comparison
            localParameterList={groupElements.parameter}
            isDataLoading={isDataLoading || loading}
            chartGraphData={chartGraphData}
            selectedParameters={selectedParameters}
            selectedDevices={selectedDevices}
            sectionName="Comparison"
            onZoom={() => setZoomedChartType('comparison')}
          />
          <ScatterPlot
            selectedParameters={selectedParameters}
            selectedDevices={selectedDevices}
            localParameterList={groupElements.parameter}
            loading={isDataLoading || loading}
            timeSpan={timeSpan}
            onZoom={() => setZoomedChartType('scatter')}
            graphData={scatterPlotData}
          />
        </div>
      )}
      {zoomedChartType && (
        <div className="w-full flex flex-col flex-1">
          {zoomedChartType === 'heatmap' && (
            <ZoomedHeatMap onBackPress={() => setZoomedChartType(null)} />
          )}
          {zoomedChartType === 'comparison' && (
            <Comparison
              localParameterList={groupElements.parameter}
              chartGraphData={chartGraphData}
              isDataLoading={isDataLoading}
              selectedParameters={selectedParameters}
              selectedDevices={selectedDevices}
              sectionName="Comparison"
              isZoomed
              onBackPress={() => setZoomedChartType(null)}
            />
          )}
          {zoomedChartType === 'trend' && (
            <Trends
              isZoomed
              selectedGroupLegends={selectedGroupLegends}
              setSelectedGroupLegends={setSelectedGroupLegends}
              groupStructure="device:parameter"
              groupElements={groupElements}
              timeSpan={timeSpan}
              isDataLoading={isDataLoading}
              labels={trendsLabel}
              lineChartGraphData={chartGraphData}
              selectedParameters={selectedParameters}
              onBackPress={() => setZoomedChartType(null)}
              type={deviceType}
            />
          )}
          {zoomedChartType === 'scatter' && (
            <ScatterPlot
              isZoomed
              graphData={scatterPlotData}
              selectedParameters={selectedParameters}
              selectedDevices={selectedDevices}
              localParameterList={groupElements.parameter}
              loading={isDataLoading || loading}
              timeSpan={timeSpan}
              onBackPress={() => setZoomedChartType(null)}
            />
          )}
        </div>
      )}
      {/* {zoomedChartType && <AlertAndSuggestions />} */}
    </>
  );
}
